import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Code2, RefreshCw } from 'lucide-react';
import { INDUSTRY_RANGES, TIER_SUMMARIES } from '../data/pricingTiers';
import PageHeader from '../components/ui/PageHeader';
import Tabs from '../components/ui/Tabs';
import HScroller from '../components/ui/HScroller';
import PricingCalculator from './PricingCalculator';

const a = 'text-primary-300 hover:text-primary-200 underline underline-offset-4';

const Compare: React.FC = () => (
  <div className="space-y-5">
    <HScroller label="All seven development tiers compared" trackClassName="rounded-2xl border border-secondary-800">
      <table className="w-full min-w-[58rem] text-sm text-left border-collapse align-top">
        <caption className="sr-only">
          All seven development tiers with their approved one-time price, a directional industry average for comparison,
          the standard reasons a project lands in each tier, and a link to the full tier description
        </caption>
        <thead className="bg-secondary-900 text-secondary-300">
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-secondary-900 px-3 py-3 font-semibold">Tier</th>
            <th scope="col" className="px-3 py-3 font-semibold whitespace-nowrap">Our one-time price</th>
            <th scope="col" className="px-3 py-3 font-semibold">Standard reasons</th>
            <th scope="col" className="px-3 py-3 font-semibold">Best for</th>
            <th scope="col" className="px-3 py-3 font-semibold">Details</th>
          </tr>
        </thead>
        <tbody>
          {TIER_SUMMARIES.map((t) => (
            <tr key={t.tier} className="border-t border-secondary-800 align-top">
              <th scope="row" className="sticky left-0 z-10 bg-secondary-950 px-3 py-4 min-w-[10rem]">
                <Link to={t.href} className="font-bold text-white hover:text-primary-200 underline-offset-4 hover:underline">
                  {t.label}
                </Link>
                <span className="mt-0.5 block text-xs font-medium text-secondary-300">{t.name}</span>
              </th>
              <td className="px-3 py-4 whitespace-nowrap">
                <span className="font-semibold text-white">{t.priceDisplay}</span>
                <span className="mt-1 block text-xs text-secondary-400">{t.priceNote}</span>
                <span className="mt-1 block text-xs text-accent-200">
                  (industry avg. {INDUSTRY_RANGES[t.tier].range} — {INDUSTRY_RANGES[t.tier].category})
                </span>
              </td>
              <td className="px-3 py-4 min-w-[18rem]">
                <ul className="space-y-1 text-secondary-200">
                  {t.reasons.map((reason) => (
                    <li key={reason} className="flex gap-2">
                      <span className="text-primary-400" aria-hidden="true">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </td>
              <td className="px-3 py-4 min-w-[14rem] text-secondary-300">{t.bestFor}</td>
              <td className="px-3 py-4 whitespace-nowrap">
                <Link to={t.href} className="inline-flex items-center gap-1.5 min-h-[44px] font-semibold text-primary-300 hover:text-white">
                  View details <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </HScroller>

    <p className="text-xs text-secondary-400">
      Industry averages are a directional benchmark, not a quote. Ranges are mapped to the closest project category from{' '}
      <a
        href="https://www.goodfirms.co/resources/website-construction-cost-survey"
        target="_blank"
        rel="noopener noreferrer"
        className={a}
      >
        GoodFirms&apos; 2026 survey of 300+ web development firms
      </a>
      : basic site $1,000–$3,000; mid-sized/CMS $5,000–$20,000; custom frontend/framework work $10,000–$40,000; custom app/portal
      $20,000–$50,000; enterprise $20,000–$200,000+. Agency prices vary widely by scope, region, and provider quality. FairCorp&apos;s
      one-time prices are the approved figures on this page.
    </p>

    <section aria-labelledby="rebuild-pricing" className="panel p-4 sm:p-6 border-primary-400/30">
      <h2 id="rebuild-pricing" className="text-lg font-bold text-white">Already have a website?</h2>
      <p className="mt-2 text-sm text-secondary-300">
        Two common starting points get a reduced development price. The reduction applies to the tier's quoted price; hosting and optional extras stay separate.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-primary-400/30 bg-primary-500/5 p-4">
          <div className="flex items-center gap-2 text-primary-200">
            <Code2 className="w-5 h-5" aria-hidden="true" />
            <h3 className="text-sm font-bold">You have substantial source code</h3>
          </div>
          <p className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">50%</span>
            <span className="text-sm text-secondary-300">of the tier's quoted price</span>
          </p>
          <p className="mt-2 text-sm text-secondary-300">
            Bring the existing codebase and FairCorp will assess it, keep what is usable, and quote the remaining work at half the tier price.
          </p>
        </div>
        <div className="rounded-xl border border-accent-400/30 bg-accent-500/5 p-4">
          <div className="flex items-center gap-2 text-accent-200">
            <RefreshCw className="w-5 h-5" aria-hidden="true" />
            <h3 className="text-sm font-bold">The source is lost, but the site is online</h3>
          </div>
          <p className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">70%</span>
            <span className="text-sm text-secondary-300">of the tier's quoted price</span>
          </p>
          <p className="mt-2 text-sm text-secondary-300">
            If the owner has lost the source code, FairCorp can rebuild from a webscrape of the live site and charge 70% of the tier's quoted price.
          </p>
        </div>
      </div>
      <p className="mt-3 text-xs text-secondary-400">
        Eligibility is confirmed after reviewing the code or the live site. The discount applies to development only, not to monthly hosting or third-party fees.
      </p>
    </section>

    <section aria-labelledby="pricing-notes" className="grid gap-3 sm:grid-cols-3">
      <div className="panel p-4">
        <h2 id="pricing-notes" className="text-sm font-bold text-white">Published starting points</h2>
        <p className="mt-2 text-sm text-secondary-300">
          Every tier has a public price range before you talk to anyone. Tier 1 starts at $200; Tier 5 starts at $18,000.
        </p>
      </div>
      <div className="panel p-4">
        <h2 className="text-sm font-bold text-white">A quote, not a checkout</h2>
        <p className="mt-2 text-sm text-secondary-300">
          The calculator gives a planning estimate. FairCorp confirms the final scope and price after a short review, and Tier 4+ always includes an engineering review.
        </p>
      </div>
      <div className="panel p-4">
        <h2 className="text-sm font-bold text-white">You own the work</h2>
        <p className="mt-2 text-sm text-secondary-300">
          Commissioned source code and accumulated data are yours to transfer. Hosting is optional and separate from development.
        </p>
      </div>
    </section>

    <p className="text-sm text-secondary-300">
      Not sure which tier fits? <Link to="/pricing?view=calculator" className={a}>Use the pricing calculator</Link> or{' '}
      <Link to="/contact?path=development" className={a}>send a development inquiry</Link>.
    </p>
  </div>
);

const Pricing: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'calculator' ? 'calculator' : 'compare';

  return (
    <div className="page-inner pb-10">
      <PageHeader
        kicker={<span className="chip-dev">Development · one-time</span>}
        title="Development pricing"
        lead={
          <>
            Seven clear tiers, from a single page to a high-reliability platform. See the price and the standard reasons a project lands in each tier before anyone writes a custom proposal.
          </>
        }
        actions={
          <>
            <Link to="/pricing?view=calculator" className="btn btn-primary">
              Find my price tier <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/plans" className="btn btn-outline">
              Hosting only? See plans
            </Link>
          </>
        }
      />

      <Tabs
        label="Development pricing views"
        size="md"
        value={view}
        onChange={(id) => setParams(id === 'calculator' ? { view: 'calculator' } : {}, { replace: true })}
        tabs={[
          { id: 'compare', label: 'Compare all 7 tiers', panel: <Compare /> },
          { id: 'calculator', label: 'Pricing calculator', panel: <PricingCalculator /> },
        ]}
      />
    </div>
  );
};

export default Pricing;
